import { useState, useRef } from 'react';
import {
  Box, Typography, TextField, CircularProgress,
  Avatar, Chip, Button, Divider,
} from '@mui/material';
import Rating from '@mui/material/Rating';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { SupportTicketStatus, SupportTicketPriority } from '../../types/support';
import { useSupport } from '../../hooks/useSupport';
import { AttachmentList } from './AttachmentChip';

dayjs.extend(relativeTime);

const STATUS_CONFIG: Record<SupportTicketStatus, { color: string; bg: string; border: string; label: string }> = {
  Open:                 { color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe', label: 'Open' },
  InProgress:           { color: '#d97706', bg: '#fffbeb', border: '#fde68a', label: 'In Progress' },
  WaitingForCustomer:   { color: '#7c3aed', bg: '#f5f3ff', border: '#ddd6fe', label: 'Waiting on You' },
  WaitingForThirdParty: { color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', label: 'On Hold' },
  Resolved:             { color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', label: 'Resolved' },
  Closed:               { color: '#9ca3af', bg: '#f9fafb', border: '#e5e7eb', label: 'Closed' },
  Reopened:             { color: '#dc2626', bg: '#fef2f2', border: '#fecaca', label: 'Reopened' },
};

const PRIORITY_DOT: Record<SupportTicketPriority, string> = {
  Critical: '#ef4444',
  High:     '#f97316',
  Normal:   '#8A70D6',
  Low:      '#9ca3af',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#f9fafb', borderRadius: '9px', fontSize: '0.8rem',
    '& fieldset': { borderColor: '#e5e7eb' },
    '&:hover fieldset': { borderColor: '#d1d5db' },
    '&.Mui-focused fieldset': { borderColor: '#8A70D6', borderWidth: 1.5 },
  },
  '& textarea::placeholder': { color: '#9ca3af', opacity: 1 },
};

interface Props {
  ticketId: number;
  onBack: () => void;
}

export function TicketDetail({ ticketId, onBack }: Props) {
  const { useTicketDetail, useAddComment, useReopenTicket, useRateTicket, useUploadAttachment } = useSupport();
  const { data: ticket, isLoading } = useTicketDetail(ticketId);
  const addComment = useAddComment();
  const reopenTicket = useReopenTicket();
  const rateTicket = useRateTicket();
  const { files, isUploading, upload, remove, clear, toJson } = useUploadAttachment();

  const [comment, setComment] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [ratingFeedback, setRatingFeedback] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) upload(file);
    e.target.value = '';
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {[80, '70%', 60, 100, 60].map((w, i) => (
          <Box key={i} sx={{
            height: typeof w === 'number' ? w : 20, width: w,
            borderRadius: '8px', bgcolor: '#f3f4f6',
            animation: 'sPulse 1.4s ease-in-out infinite',
            animationDelay: `${i * 0.1}s`,
            '@keyframes sPulse': { '0%,100%': { opacity: 0.5 }, '50%': { opacity: 1 } },
          }} />
        ))}
      </Box>
    );
  }
  if (!ticket) return null;

  const status = ticket.status as SupportTicketStatus;
  const priority = ticket.priority as SupportTicketPriority;
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.Open;
  const dot = PRIORITY_DOT[priority] ?? '#9ca3af';
  const canReopen = status === 'Resolved' || status === 'Closed';
  const showRating = canReopen && !ticket.satisfactionRating;
  const canComment = status !== 'Closed';

  const handleSendComment = () => {
    if (!comment.trim()) return;
    addComment.mutate(
      { id: ticketId, payload: { comment: comment.trim(), attachmentsJson: toJson() } },
      { onSuccess: () => { setComment(''); clear(); } },
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

      {/* Back */}
      <Box
        onClick={onBack}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 2,
          color: '#6b7280', cursor: 'pointer', width: 'fit-content',
          fontSize: '0.72rem', fontWeight: 600,
          transition: 'color 0.15s',
          '&:hover': { color: '#8A70D6' },
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        All Tickets
      </Box>

      {/* Ticket header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.62rem', color: '#9ca3af', letterSpacing: '0.04em' }}>
            {ticket.ticketNumber}
          </Typography>
          <Box sx={{ width: 2, height: 2, borderRadius: '50%', bgcolor: '#d1d5db' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.375 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: dot }} />
            <Typography sx={{ fontSize: '0.62rem', color: dot, fontWeight: 600 }}>{priority}</Typography>
          </Box>
          <Box sx={{
            ml: 'auto', px: 0.75, py: 0.2, borderRadius: '5px',
            bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
            fontSize: '0.6rem', fontWeight: 700,
          }}>
            {cfg.label}
          </Box>
        </Box>

        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827', lineHeight: 1.4, mb: 0.875 }}>
          {ticket.subject}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {ticket.module && (
            <Box sx={{ px: 0.75, py: 0.2, borderRadius: '5px', bgcolor: '#ede9fe', border: '1px solid #ddd6fe', fontSize: '0.62rem', fontWeight: 600, color: '#8A70D6' }}>
              {ticket.module}
            </Box>
          )}
          {ticket.category && (
            <Box sx={{ px: 0.75, py: 0.2, borderRadius: '5px', bgcolor: '#f3f4f6', border: '1px solid #e5e7eb', fontSize: '0.62rem', color: '#6b7280' }}>
              {ticket.category.replace(/([A-Z])/g, ' $1').trim()}
            </Box>
          )}
          <Box sx={{ px: 0.75, py: 0.2, borderRadius: '5px', bgcolor: '#f9fafb', border: '1px solid #e5e7eb', fontSize: '0.6rem', color: '#9ca3af' }}>
            {dayjs(ticket.createdOn).format('DD MMM YYYY')}
          </Box>
        </Box>
      </Box>

      {/* Description */}
      <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#f9fafb', border: '1px solid #f0f0f0', mb: 1.75 }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
          {ticket.description}
        </Typography>
        <AttachmentList json={ticket.attachmentsJson} />
      </Box>

      {/* Resolution */}
      {ticket.resolution && (
        <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', mb: 1.75 }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#16a34a', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.5 }}>
            Resolution
          </Typography>
          <Typography sx={{ fontSize: '0.77rem', color: '#374151', lineHeight: 1.7 }}>
            {ticket.resolution}
          </Typography>
          {ticket.resolvedAt && (
            <Typography sx={{ fontSize: '0.62rem', color: '#86efac', mt: 0.5 }}>
              Resolved {dayjs(ticket.resolvedAt).fromNow()}
            </Typography>
          )}
        </Box>
      )}

      {/* Rating */}
      {showRating && (
        <Box sx={{ p: 1.5, borderRadius: '10px', bgcolor: '#fffbeb', border: '1px solid #fde68a', mb: 1.75 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#92400e', mb: 0.75 }}>
            How was your support experience?
          </Typography>
          <Rating
            value={rating}
            onChange={(_, v) => setRating(v)}
            size="small"
          />
          {rating !== null && (
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <TextField
                size="small" fullWidth multiline maxRows={2}
                placeholder="Optional feedback…"
                value={ratingFeedback}
                onChange={(e) => setRatingFeedback(e.target.value)}
                sx={fieldSx}
              />
              <Button
                size="small" variant="contained"
                onClick={() => rateTicket.mutate({ id: ticketId, payload: { rating: rating!, feedback: ratingFeedback || undefined } })}
                disabled={rateTicket.isPending}
                sx={{ alignSelf: 'flex-start', borderRadius: '7px', textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }}
              >
                Submit Rating
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Conversation */}
      <Divider sx={{ borderColor: '#f0f0f0', mb: 1.75 }} />
      <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase', mb: 1.25 }}>
        Conversation {ticket.comments.length > 0 && `· ${ticket.comments.length}`}
      </Typography>

      {ticket.comments.length === 0 && (
        <Typography sx={{ fontSize: '0.73rem', color: '#9ca3af', textAlign: 'center', py: 2, mb: 1.5 }}>
          No replies yet — we'll get back to you soon
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {ticket.comments.map((c) => (
          <Box key={c.id} sx={{ display: 'flex', flexDirection: c.isFromSupport ? 'row' : 'row-reverse', gap: 0.875, alignItems: 'flex-end' }}>
            <Avatar sx={{
              width: 26, height: 26, fontSize: '0.62rem', fontWeight: 700, flexShrink: 0,
              bgcolor: c.isFromSupport ? '#ede9fe' : '#f3f4f6',
              color: c.isFromSupport ? '#8A70D6' : '#6b7280',
            }}>
              {c.isFromSupport ? (c.commentByName?.[0]?.toUpperCase() ?? 'S') : 'U'}
            </Avatar>
            <Box sx={{ maxWidth: '82%' }}>
              <Box sx={{
                p: '10px 12px',
                borderRadius: c.isFromSupport ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
                bgcolor: c.isFromSupport ? '#f5f3ff' : '#f3f4f6',
                border: `1px solid ${c.isFromSupport ? '#ddd6fe' : '#e5e7eb'}`,
              }}>
                {c.isFromSupport && c.commentByName && (
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#8A70D6', mb: 0.375 }}>
                    {c.commentByName}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.78rem', color: '#374151', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                  {c.comment}
                </Typography>
                <AttachmentList json={c.attachmentsJson} />
              </Box>
              <Typography sx={{
                fontSize: '0.6rem', color: '#9ca3af', mt: 0.375, px: 0.5,
                textAlign: c.isFromSupport ? 'left' : 'right',
              }}>
                {dayjs(c.createdOn).fromNow()}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Reopen */}
      {canReopen && (
        <Button
          fullWidth variant="outlined" size="small"
          onClick={() => reopenTicket.mutate(ticketId)}
          disabled={reopenTicket.isPending}
          sx={{
            mb: 1.75, borderRadius: '8px', textTransform: 'none',
            fontWeight: 700, fontSize: '0.75rem',
            borderColor: '#fca5a5', color: '#dc2626',
            '&:hover': { borderColor: '#f87171', bgcolor: '#fef2f2' },
          }}
        >
          {reopenTicket.isPending ? <CircularProgress size={14} /> : '↩ Reopen Ticket'}
        </Button>
      )}

      {/* Reply composer */}
      {canComment && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
          {files.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {files.map((f) => (
                <Chip key={f.blobPath} label={f.name} size="small" onDelete={() => remove(f.blobPath)}
                  sx={{ height: 22, fontSize: '0.65rem', bgcolor: '#ede9fe', color: '#8A70D6', border: '1px solid #ddd6fe', '& .MuiChip-deleteIcon': { color: '#a78bfa', '&:hover': { color: '#8A70D6' } } }}
                />
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <TextField
                fullWidth size="small"
                placeholder="Write a reply… (Ctrl+Enter to send)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                multiline maxRows={4}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) handleSendComment(); }}
                sx={{ ...fieldSx, '& .MuiOutlinedInput-root': { ...fieldSx['& .MuiOutlinedInput-root'], pr: '36px' } }}
              />
              <input ref={fileRef} type="file" hidden onChange={handleFileChange} accept="image/*,.pdf,.doc,.docx,.xlsx,.csv,.txt" />
              <Box
                onClick={() => fileRef.current?.click()}
                sx={{
                  position: 'absolute', right: 8, bottom: 8,
                  width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: '#9ca3af', transition: 'color 0.15s',
                  '&:hover': { color: '#8A70D6' },
                }}
              >
                {isUploading
                  ? <CircularProgress size={12} sx={{ color: '#8A70D6' }} />
                  : <AttachFileIcon sx={{ fontSize: 14 }} />
                }
              </Box>
            </Box>
            <Box
              component="button"
              onClick={handleSendComment}
              disabled={!comment.trim() || addComment.isPending}
              sx={{
                width: 36, height: 36, borderRadius: '9px', border: 'none', flexShrink: 0,
                bgcolor: comment.trim() ? '#8A70D6' : '#f3f4f6',
                color: comment.trim() ? '#fff' : '#9ca3af',
                cursor: comment.trim() ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s', alignSelf: 'flex-end',
                '&:hover': comment.trim() ? { bgcolor: '#7a60c6', boxShadow: '0 2px 8px rgba(138,112,214,0.3)' } : {},
              }}
            >
              {addComment.isPending
                ? <CircularProgress size={13} sx={{ color: '#fff' }} />
                : <SendIcon sx={{ fontSize: 15 }} />
              }
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
